import { useTranslation } from "react-i18next";
import { Button, Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { BrandGroupSelect, DealersAndLocationsDropdown } from "components";
import { createFormHelper } from "lib/Form";
import { FACTORYEXPORT_FREQUENCY, FACTORYEXPORT_QUERYRANGE, FACTORY_REPORTS } from "models";
import { EmailMessageEditor } from "modules/FactoryExportsDashboard/components/ExportModal/components";
import { useReportsQuery } from "modules/FactoryExportsDashboard/hooks";
import { getWeekDayOptions } from "modules/FactoryExportsDashboard/utils";
import { capitalizeFirstLetter, enumToDropdownOptions } from "utils/common";

import type { DealersLocationsDropdownValue, DropdownItemProps } from "components";
import type { FactoryExportFormPayload } from "modules/FactoryExportsDashboard/components/ExportModal/hooks";

type ExportFormProps = {
  isEdit: boolean;
  isPending: boolean;
  onClose: () => void;
};

const FormHelper = createFormHelper<FactoryExportFormPayload>();

const LOCATION_OVERVIEW_REPORTS = [FACTORY_REPORTS.LocationOverview, FACTORY_REPORTS.StellantisNL, FACTORY_REPORTS.LocationOverviewV8];
const isLocationOverviewReport = (factoryReportId: FACTORY_REPORTS | null) => factoryReportId !== null && LOCATION_OVERVIEW_REPORTS.includes(factoryReportId);

const MONTH_DAYS_COUNT = 28;
const getMonthDayOptions = (): DropdownItemProps[] => Array.from({ length: MONTH_DAYS_COUNT }, (_, idx) => ({ key: idx + 1, value: idx + 1, text: `${idx + 1}` }));

export const ExportForm = ({ isEdit, isPending, onClose }: ExportFormProps) => {
  const { t } = useTranslation();

  const { data: reportOptions = [] } = useReportsQuery();
  const { setValue, watch, formState } = FormHelper.useFormContext();

  const [name, emails, frequency, day, queryRange, factoryReportId, dealerIds, dealerLocationIds] = watch([
    "name",
    "emails",
    "frequency",
    "day",
    "query_range",
    "factory_report_id",
    "dealer_ids",
    "dealer_location_ids"
  ]);

  const frequencyOptions = enumToDropdownOptions({ enumObj: FACTORYEXPORT_FREQUENCY, t });
  const queryRangeOptions = enumToDropdownOptions({ enumObj: FACTORYEXPORT_QUERYRANGE, t });

  const isEveryDay = frequency === FACTORYEXPORT_FREQUENCY.EveryDay;
  const isEveryMonth = frequency === FACTORYEXPORT_FREQUENCY.EveryMonth;
  const showDayField = frequency !== null && !isEveryDay;
  const showBrandGroupsField = isLocationOverviewReport(factoryReportId);
  const hasNoDealerOrLocation = !dealerIds?.length && !dealerLocationIds?.length;

  const handleReportChange = (selectedReportId?: FACTORY_REPORTS | null) => {
    if (!isLocationOverviewReport(selectedReportId ?? null)) setValue("brand_group_ids", []);
  };

  const handleDealersLocationsChange = ({ dealerIds, locationIds }: DealersLocationsDropdownValue) => {
    setValue("dealer_ids", dealerIds);
    setValue("dealer_location_ids", locationIds);
  };

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-title">{isEdit ? t("v8_edit_export", "Edit export") : t("v8_add_export", "Add export")}</div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container" scrolling>
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={formState.isSubmitted && !name.trim()}>
              <label>{t("v8_export_name", "Export name")}</label>
              <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_export_name", "Export name"), fluid: true }} />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field required error={formState.isSubmitted && factoryReportId === null}>
              <label>{t("v8_choose_report", "Choose report")}</label>
              <FormHelper.Field.Dropdown
                name="factory_report_id"
                options={reportOptions}
                onChange={handleReportChange}
                dropdownProps={{
                  placeholder: t("v8_choose_report", "Choose report"),
                  disabled: isEdit,
                  search: true,
                  clearable: true,
                  selectOnBlur: false
                }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={formState.isSubmitted && !emails}>
              <label>{t("v8_recipients_emails", "Recipient's emails")}</label>
              <FormHelper.Field.TextArea name="emails" textAreaProps={{ placeholder: t("v8_comma_separated_emails", "Comma separated email addresses"), rows: 3 }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={formState.isSubmitted && frequency === null}>
              <label>{t("v8_frequency", "Frequency")}</label>
              <FormHelper.Field.Dropdown
                name="frequency"
                options={frequencyOptions}
                dropdownProps={{ placeholder: t("v8_frequency", "Frequency"), search: true, clearable: true, selectOnBlur: false }}
              />
            </Form.Field>
          </GridColumn>

          {showDayField && (
            <GridColumn>
              <Form.Field required error={formState.isSubmitted && day === null}>
                <label>
                  {isEveryMonth ? t("v8_select_a_day_of_the_month", "Select a day of the month") : t("v8_select_a_day_of_the_week", "Select a day of the week")}
                </label>
                <FormHelper.Field.Dropdown
                  name="day"
                  options={isEveryMonth ? getMonthDayOptions() : getWeekDayOptions(t)}
                  dropdownProps={{ placeholder: capitalizeFirstLetter(t("v8_day", "Day")), search: true, clearable: true, selectOnBlur: false }}
                />
              </Form.Field>
            </GridColumn>
          )}

          <GridColumn>
            <Form.Field required error={formState.isSubmitted && queryRange === null}>
              <label>{t("v8_query_range", "Query range")}</label>
              <FormHelper.Field.Dropdown
                name="query_range"
                options={queryRangeOptions}
                dropdownProps={{ placeholder: t("v8_query_range", "Query range"), search: true, clearable: true, selectOnBlur: false }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={formState.isSubmitted && hasNoDealerOrLocation}>
              <label>{t("v8_dealer_location", "Dealer/Location")}</label>
              <DealersAndLocationsDropdown
                allowSelectAllDealers={false}
                values={{ dealerIds: dealerIds ?? [], locationIds: dealerLocationIds ?? [] }}
                handleOnChange={handleDealersLocationsChange}
              />
            </Form.Field>
          </GridColumn>

          {showBrandGroupsField && (
            <GridColumn>
              <Form.Field>
                <label>{t("v8_brand_groups", "Brand Groups")}</label>
                <FormHelper.Controller name="brand_group_ids" render={({ field }) => <BrandGroupSelect value={field.value ?? []} onChange={field.onChange} />} />
              </Form.Field>
            </GridColumn>
          )}
        </Grid>

        <Grid columns="equal">
          <GridColumn width={10}>
            <Form.Field>
              <label>{t("v8_email_subject", "Email subject")}</label>
              <FormHelper.Field.Input name="email_subject" inputProps={{ placeholder: t("v8_email_subject", "Email subject"), fluid: true }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn width={4}>
            <FormHelper.Field.Checkbox name="is_ams_timezone" label={t("v8_amsterdam_timezone", "Amsterdam timezone")} />
          </GridColumn>

          <GridColumn width={4}>
            <FormHelper.Field.Checkbox name="is_ods_format" label={t("v8_ods_format", "ODS format")} />
          </GridColumn>
        </Grid>

        <EmailMessageEditor />
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="action-buttons-container">
          <Button type="button" className="light" onClick={onClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>
          <Button type="submit" color="green" disabled={isPending} loading={isPending}>
            {t("v8_save", "Save")}
            <Icon className="check" />
          </Button>
        </div>
      </Modal.Actions>
    </>
  );
};
